import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Package2, Plus, Building2 } from 'lucide-react';
import { StockItem } from '../lib/stockItems';
import { supabase } from '../lib/supabase';
import { getSuppliers, createSupplier, Customer } from '../lib/customers';
import { useBusiness } from '../contexts/BusinessContext';

interface EditStockModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  item: StockItem | null;
}

export default function EditStockModal({
  isOpen,
  onClose,
  onSuccess,
  item
}: EditStockModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suppliers, setSuppliers] = useState<Customer[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<string | null>(null);
  const [isAddingSupplier, setIsAddingSupplier] = useState(false);
  const [isSelectingSupplier, setIsSelectingSupplier] = useState(false);
  const [supplierFormData, setSupplierFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sku: '',
    unit: '',
    price: '',
    acquisition_cost: '',
    quantity: '',
    reorder_point: '',
    max_quantity: ''
  });

  useEffect(() => {
    if (isOpen && item) {
      setIsAnimating(true);
      setFormData({
        name: item.name,
        description: item.description || '',
        sku: item.sku || '',
        unit: item.unit,
        price: item.price.toString(),
        acquisition_cost: item.acquisition_cost.toString(),
        quantity: item.quantity.toString(),
        reorder_point: item.reorder_point.toString(),
        max_quantity: item.max_quantity.toString()
      });
      setSelectedSupplier(item.supplier_id);
      
      // Load suppliers when modal opens
      if (selectedBusiness?.id) {
        loadSuppliers();
      }
    }
  }, [isOpen, item, selectedBusiness?.id]);

  const loadSuppliers = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      const data = await getSuppliers(selectedBusiness.id);
      setSuppliers(data);
    } catch (err) {
      console.error('Error loading suppliers:', err);
    }
  };

  // Get the currently selected supplier details
  const getSelectedSupplierDetails = () => {
    if (!selectedSupplier) return null;
    return suppliers.find(supplier => supplier.id === selectedSupplier);
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setError(null);
    }
  };

  const handleAddSupplier = async () => {
    if (!selectedBusiness?.id) return;
    
    if (!supplierFormData.first_name || !supplierFormData.email) {
      setError('Supplier first name and email are required');
      return;
    }
    
    try {
      setLoading(true);
      
      const supplier = await createSupplier({
        business_id: selectedBusiness.id,
        type: 'person',
        first_name: supplierFormData.first_name,
        last_name: supplierFormData.last_name,
        email: supplierFormData.email,
        phone: supplierFormData.phone || null,
        address: null,
        source: null,
        tags: [],
        last_interaction: null,
        lifetime_value: 0,
        next_service_date: null,
        referral_source: null,
        preferences: {},
        status: 'active',
        amount_spent: 0,
        credit_balance: 0,
        notes: null,
        tax_exempt: false,
        workflow_exempt: false,
        carfax_exempt: false,
        receive_reminders: false,
        receive_offers: false
      });
      
      await loadSuppliers();
      setSelectedSupplier(supplier.id);
      setIsAddingSupplier(false);
      setSupplierFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: ''
      });
    } catch (err) {
      console.error('Error creating supplier:', err);
      setError(err instanceof Error ? err.message : 'Failed to create supplier');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!item) return;

    try {
      setLoading(true);
      setError(null);

      // Validate inputs
      const maxQuantity = parseInt(formData.max_quantity);
      const reorderPoint = parseInt(formData.reorder_point);
      
      if (maxQuantity <= reorderPoint) {
        throw new Error('Maximum quantity must be greater than reorder point');
      }

      if (!supabase) {
        throw new Error('Database connection not available');
      }

      // Update stock item
      const { error: updateError } = await supabase
        .from('stock_items')
        .update({
          name: formData.name,
          description: formData.description || null,
          sku: formData.sku || null,
          unit: formData.unit,
          price: parseFloat(formData.price),
          acquisition_cost: parseFloat(formData.acquisition_cost),
          quantity: parseInt(formData.quantity),
          reorder_point: parseInt(formData.reorder_point),
          max_quantity: maxQuantity,
          supplier_id: selectedSupplier
        })
        .eq('id', item.id);

      if (updateError) throw updateError;

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error updating stock item:', err);
      setError(err instanceof Error ? err.message : 'Failed to update stock item');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;
  if (!item) return null;

  const modalContent = (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[100020] flex items-center justify-center p-4`}
      onClick={onClose}
    >
      <div 
        className={`bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] transform transition-all duration-300 ease-out ${
          isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-gray-200 bg-white rounded-t-2xl flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Edit Stock Item</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100">
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Item Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SKU
                  </label>
                  <input
                    type="text"
                    value={formData.sku}
                    onChange={(e) => setFormData(prev => ({ ...prev, sku: e.target.value }))}
                    className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unit
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.unit}
                    onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))}
                    className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Retail Price
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      required
                      value={formData.price}
                      onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                      className="w-full pl-7 pr-4 h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Acquisition Cost
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      required
                      value={formData.acquisition_cost}
                      onChange={(e) => setFormData(prev => ({ ...prev, acquisition_cost: e.target.value }))}
                      className="w-full pl-7 pr-4 h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Quantity
                  </label>
                  <input
                    type="number"
                    min="0"
                    required
                    value={formData.quantity}
                    onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
                    className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reorder Point
                  </label>
                  <input
                    type="number"
                    min="0"
                    required
                    value={formData.reorder_point}
                    onChange={(e) => setFormData(prev => ({ ...prev, reorder_point: e.target.value }))}
                    className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Quantity
                  </label>
                  <input
                    type="number"
                    min="1"
                    required
                    value={formData.max_quantity}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_quantity: e.target.value }))}
                    className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                  />
                </div>
              </div>



              {/* Supplier Selection */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-4">Supplier</h3>
                
                {/* Current Supplier Display */}
                <div className="border border-gray-200 rounded-lg p-4">
                  {getSelectedSupplierDetails() ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center mr-3">
                          <Building2 className="h-5 w-5 text-gray-400" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {getSelectedSupplierDetails()?.first_name} {getSelectedSupplierDetails()?.last_name}
                          </div>
                          {getSelectedSupplierDetails()?.email && (
                            <div className="text-xs text-gray-500">{getSelectedSupplierDetails()?.email}</div>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setIsSelectingSupplier(true)}
                        className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Change
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center mr-3">
                          <Building2 className="h-5 w-5 text-gray-400" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">No supplier</div>
                          <div className="text-xs text-gray-500">No supplier assigned</div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setIsSelectingSupplier(true)}
                        className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Select
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-8">
              <button
                type="submit"
                disabled={loading}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Supplier Selection Modal */}
      {isSelectingSupplier && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[100030]"
          onClick={() => setIsSelectingSupplier(false)}
          style={{
            transform: 'translateZ(0)',
            WebkitTransform: 'translateZ(0)',
            isolation: 'isolate',
            touchAction: 'manipulation',
            WebkitTouchCallout: 'none',
            WebkitUserSelect: 'none',
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
          }}
        >
          <div 
            className="fixed inset-y-0 right-0 w-full md:w-[500px] backdrop-blur-xl bg-white/80 transform transition-transform duration-300 ease-out shadow-2xl translate-x-0"
            style={{ 
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="relative bg-gray-800 border-b border-gray-700 shadow-lg">
                <div className="flex items-center justify-between px-2 py-2 sm:px-6 sm:py-4">
                  <div className="flex items-center space-x-2 sm:space-x-4 flex-1 min-w-0">
                    <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center shadow-sm">
                      <Building2 className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-base sm:text-lg font-bold text-white truncate">
                        Select Supplier
                      </h2>
                      <div className="text-xs sm:text-sm text-gray-300 font-medium">
                        Choose a supplier for this item
                      </div>
                    </div>
                  </div>
                  <button 
                    onClick={() => setIsSelectingSupplier(false)} 
                    className="flex items-center justify-center w-8 h-8 sm:w-11 sm:h-11 rounded-lg sm:rounded-full bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 touch-manipulation shadow-sm"
                    style={{ minWidth: '32px', minHeight: '32px' }}
                  >
                    <X className="h-4 w-4 sm:h-6 sm:w-6" />
                  </button>
                </div>
              </div>

              {/* Content Area */}
              <div className="flex-1 overflow-y-auto p-2 sm:p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="space-y-3">
                  {/* No Supplier Option */}
                  <div
                    onClick={() => {
                      setSelectedSupplier(null);
                      setIsSelectingSupplier(false);
                    }}
                    className={`backdrop-blur-xl bg-white/60 rounded-2xl border border-purple-200/30 p-4 hover:bg-purple-50/50 hover:border-purple-300/50 cursor-pointer transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation ${
                      selectedSupplier === null ? 'bg-purple-50/70 border-purple-300/50' : ''
                    }`}
                    style={{ 
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      minHeight: '80px'
                    }}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center text-sm font-bold text-white shadow-sm flex-shrink-0">
                        <Building2 className="h-6 w-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-gray-900">No supplier</div>
                        <div className="text-sm text-gray-600 font-medium">Remove supplier assignment</div>
                      </div>
                    </div>
                  </div>

                  {/* Existing Suppliers */}
                  {suppliers.map((supplier) => (
                    <div
                      key={supplier.id}
                      onClick={() => {
                        setSelectedSupplier(supplier.id);
                        setIsSelectingSupplier(false);
                      }}
                      className={`backdrop-blur-xl bg-white/60 rounded-2xl border border-purple-200/30 p-4 hover:bg-purple-50/50 hover:border-purple-300/50 cursor-pointer transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation ${
                        selectedSupplier === supplier.id ? 'bg-purple-50/70 border-purple-300/50' : ''
                      }`}
                      style={{ 
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        minHeight: '80px'
                      }}
                    >
                      <div className="flex items-center space-x-4">
                        <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-sm font-bold text-white shadow-sm flex-shrink-0">
                          {supplier.first_name?.[0]}{supplier.last_name?.[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold text-gray-900 truncate">
                            {supplier.first_name} {supplier.last_name}
                          </div>
                          {supplier.email && (
                            <div className="text-sm text-gray-600 font-medium truncate">
                              {supplier.email}
                            </div>
                          )}
                          {supplier.phone && (
                            <div className="text-sm text-gray-500 font-medium">
                              {supplier.phone}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Add New Supplier */}
                  <div
                    onClick={() => {
                      setIsSelectingSupplier(false);
                      setIsAddingSupplier(true);
                    }}
                    className="backdrop-blur-xl bg-white/60 rounded-2xl border-2 border-dashed border-purple-300/50 p-4 hover:bg-purple-50/50 hover:border-purple-400/50 cursor-pointer transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
                    style={{ 
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      minHeight: '80px'
                    }}
                  >
                    <div className="flex items-center justify-center space-x-3">
                      <Plus className="h-5 w-5 text-purple-600" />
                      <span className="text-sm font-bold text-purple-600">Add New Supplier</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add New Supplier Modal */}
      {isAddingSupplier && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[100040] flex items-center justify-center p-4"
          onClick={() => setIsAddingSupplier(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
            onClick={e => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Add New Supplier</h3>
              <button 
                onClick={() => setIsAddingSupplier(false)} 
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        required
                        value={supplierFormData.first_name}
                        onChange={(e) => setSupplierFormData(prev => ({ ...prev, first_name: e.target.value }))}
                        className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                        placeholder="Enter first name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={supplierFormData.last_name}
                        onChange={(e) => setSupplierFormData(prev => ({ ...prev, last_name: e.target.value }))}
                        className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                        placeholder="Enter last name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        required
                        value={supplierFormData.email}
                        onChange={(e) => setSupplierFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                        placeholder="Enter email address"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={supplierFormData.phone}
                        onChange={(e) => setSupplierFormData(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                        placeholder="Enter phone number (optional)"
                      />
                </div>
                    </div>

              <div className="flex justify-end space-x-3 mt-6">
                      <button
                        type="button"
                        onClick={() => setIsAddingSupplier(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleAddSupplier}
                        disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 disabled:opacity-50"
                      >
                        {loading ? 'Adding...' : 'Add Supplier'}
                      </button>
              </div>
            </div>
            </div>
        </div>
      )}
    </div>
  );

  // 🚀 FIX: Use React Portal to render modal outside parent DOM tree
  // This prevents PWA stacking context issues and ensures modal appears above navigation
  return createPortal(modalContent, document.body);
}